import { useCallback, useState } from 'react';
import { Coordinate } from 'ol/coordinate';
import {
  getTargetInfoAtPixel,
  getTargetInfoName,
  ShipInfo,
  TargetTypeEnum,
  useSession,
} from '@ais-ui/websockets';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { useAisReportAge } from '../../Targets/useAISReportAge';

interface ShipSelectionData {
  shipName: string;
  speedOverGroundInKnots: number | string;
  aisReportAge: number;
  isAisReportOld: boolean;
  isSelected: boolean;
}

interface UseShipSelectionReturn {
  shipData: ShipSelectionData;
  selectShipAtCoordinate: (coordinate: Coordinate) => Promise<void>;
  resetSelection: () => void;
  isSelecting: boolean;
  updateSpeed: (speed: number | string) => void;
  setShipDirectly: (name: string, speed: number) => void;
}

export const useShipSelection = (): UseShipSelectionReturn => {
  const [shipData, setShipData] = useState<ShipSelectionData>({
    shipName: 'no ship selected',
    speedOverGroundInKnots: '',
    aisReportAge: 0,
    isAisReportOld: false,
    isSelected: false,
  });
  const [isSelecting, setIsSelecting] = useState(false);

  const { getAisReportAge } = useAisReportAge();
  const { map } = useOpenLayersContext();
  const { olSession } = useSession();

  // Helper function to convert coordinate to pixel
  const coordinateToPixel = useCallback(
    (coordinate: Coordinate) => {
      const [xFloat, yFloat] = map.getPixelFromCoordinate(coordinate);
      return {
        x: Math.round(xFloat),
        y: Math.round(yFloat),
      };
    },
    [map],
  );

  // Helper function to extract speed from ship info
  const extractSpeedFromShip = useCallback(
    (targetInfo: any, targetType: TargetTypeEnum): number => {
      if (targetType !== TargetTypeEnum.Ship) {
        return 0;
      }

      const shipInfo = targetInfo as ShipInfo;
      const numericSpeedOverGround = shipInfo.speedOverGround.replace(
        /[^0-9.]/g,
        '',
      );
      return parseFloat(numericSpeedOverGround) || 0;
    },
    [],
  );

  // Helper function to handle successful ship selection
  const handleShipSelected = useCallback(
    (targetInfoFromClick: any) => {
      const targetInfo = targetInfoFromClick.extractedTargetInfo[0];
      const targetType = targetInfoFromClick.extractedTargetInfoType;

      const speed = extractSpeedFromShip(targetInfo, targetType);
      const name = getTargetInfoName(targetInfo, targetType);
      const aisData = getAisReportAge(targetInfo, targetType);

      setShipData({
        shipName: name,
        speedOverGroundInKnots: speed,
        aisReportAge: aisData.aisReportAge,
        isAisReportOld: aisData.isAisReportOld,
        isSelected: true,
      });
    },
    [extractSpeedFromShip, getAisReportAge],
  );

  // Helper function to handle no ship selection
  const handleNoShipSelected = useCallback(() => {
    setShipData({
      shipName: 'no ship selected',
      speedOverGroundInKnots: '',
      aisReportAge: 0,
      isAisReportOld: false,
      isSelected: false,
    });
  }, []);

  // Helper function to handle ship selection error
  const handleShipSelectionError = useCallback((error: Error) => {
    console.error('Error selecting ship for measurement:', error);
    setShipData({
      shipName: 'error selecting ship',
      speedOverGroundInKnots: '',
      aisReportAge: 0,
      isAisReportOld: false,
      isSelected: false,
    });
  }, []);

  // Main ship selection function
  const selectShipAtCoordinate = useCallback(
    async (coordinate: Coordinate) => {
      if (isSelecting) return; // Prevent multiple simultaneous selections

      setIsSelecting(true);

      try {
        const pixelCoordinate = coordinateToPixel(coordinate);

        const targetInfoFromClick = await getTargetInfoAtPixel(
          olSession,
          pixelCoordinate.x,
          pixelCoordinate.y,
          false,
          false,
        );

        if (
          !targetInfoFromClick ||
          targetInfoFromClick.extractedTargetInfo.length === 0
        ) {
          handleNoShipSelected();
        } else {
          handleShipSelected(targetInfoFromClick);
        }
      } catch (error) {
        handleShipSelectionError(error as Error);
      } finally {
        setIsSelecting(false);
      }
    },
    [
      isSelecting,
      coordinateToPixel,
      olSession,
      handleNoShipSelected,
      handleShipSelected,
      handleShipSelectionError,
    ],
  );

  // Reset selection function
  const resetSelection = useCallback(() => {
    handleNoShipSelected();
  }, [handleNoShipSelected]);

  // Update speed function for manual input
  const updateSpeed = useCallback((speed: number | string) => {
    setShipData((prev) => ({
      ...prev,
      speedOverGroundInKnots: speed,
    }));
  }, []);

  // Set ship data directly (e.g. from current selection context)
  const setShipDirectly = useCallback((name: string, speed: number) => {
    setShipData({
      shipName: name,
      speedOverGroundInKnots: speed,
      aisReportAge: 0,
      isAisReportOld: false,
      isSelected: true,
    });
  }, []);

  return {
    shipData,
    selectShipAtCoordinate,
    resetSelection,
    isSelecting,
    updateSpeed,
    setShipDirectly,
  };
};
